import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';
import { apiCall } from '../utils/api';

const UserDashboard = () => {
  const [userData, setUserData] = useState(null);
  const [assignedTeacher, setAssignedTeacher] = useState(null);
  const [programInfo, setProgramInfo] = useState(null);
  const [feedbackData, setFeedbackData] = useState({ rating: 5, comment: '' });
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [teacherFeedback, setTeacherFeedback] = useState([]);
  const [givenFeedback, setGivenFeedback] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const loadDashboardData = async () => {
      // Get user data from localStorage for initial auth check
      const storedUserData = localStorage.getItem('userData');
      if (!storedUserData) {
        navigate('/login');
        return;
      }

      const localUser = JSON.parse(storedUserData);
      if (localUser.role !== 'user') {
        navigate('/');
        return;
      }

      try {
        // Fetch fresh user data from API
        // Assuming we have an endpoint /auth/me or similar, or just fetch by ID if we trust local ID.
        // But standard is /users/:id or profile.
        // Let's use the ID from localUser.
        // If userController allows fetching self...
        // Or just trust localStorage for now but fetch teacher dynamically.
        // Better: fetch user from API.
        // Assuming `localUser` has `_id` or `id`.

        // Allow fetching specific user (if backend allows). 
        // If regular user cannot fetch /users/:id, this might fail unless they are fetching themselves.
        // Let's rely on what we have, but try to fetch teacher.

        setUserData(localUser);

        // Fetch Teacher
        if (localUser.assignedTeacherId) {
          try {
            // Try to fetch teacher by ID
            // If this fails (auth), we might need a public teacher endpoint or open generic user endpoint.
            // Assuming /users/:id works for authenticated users to view others (or at least teachers).
            const teacherData = await apiCall(`/users/${localUser.assignedTeacherId}`);
            if (teacherData) {
              setAssignedTeacher({
                ...teacherData,
                photo: teacherData.photo || "teacher1.jpg", // fallback
                rating: 4.8 // Mock rating if not in DB
              });
            }
          } catch (err) {
            console.log("Could not fetch assigned teacher", err);
          }
        }

        // Fetch my feedbacks given
        // const myFeedbacks = await apiCall(`/feedback?giverId=${userId}`);
        // setGivenFeedback(myFeedbacks);

      } catch (error) {
        console.error(error);
      }

      // Mock program info (keep mock for now)
      setProgramInfo({
        name: "Beginner Yoga Foundation",
        startDate: "2024-01-15",
        endDate: "2024-06-15",
        schedule: "Mon, Wed, Fri - 6:00 AM to 7:30 AM",
        status: "Active",
        progress: 65,
        totalClasses: 48,
        completedClasses: 31,
        upcomingClasses: [
          { date: "2024-01-20", time: "6:00 AM", topic: "Sun Salutation Variations" },
          { date: "2024-01-22", time: "6:00 AM", topic: "Pranayama Techniques" },
          { date: "2024-01-24", time: "6:00 AM", topic: "Meditation & Mindfulness" }
        ]
      });

      // Mock teacher feedback (received)
      setTeacherFeedback([]);
    };

    loadDashboardData();
  }, [navigate]);


  const handleProfileUpdate = () => {
    alert('Profile update feature coming soon!');
  };

  const handleFeedbackChange = (e) => {
    const { name, value } = e.target;
    setFeedbackData({ ...feedbackData, [name]: value });
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackData.comment.trim()) {
      alert('Please enter your feedback comment');
      return;
    }

    const payload = {
      giverId: userData.id || userData._id,
      giverName: userData.name,
      giverType: 'student',
      recipientId: assignedTeacher.id || assignedTeacher._id,
      recipientName: assignedTeacher.name,
      recipientType: 'teacher',
      rating: feedbackData.rating,
      comment: feedbackData.comment
    };

    try {
      await apiCall('/feedback', 'POST', payload);

      setGivenFeedback([...givenFeedback, payload]); // Optimistic update
      setFeedbackData({ rating: 5, comment: '' });
      setFeedbackGiven(true);
      setFeedbackSubmitted(true);
      setTimeout(() => {
        setFeedbackSubmitted(false);
        setFeedbackGiven(false);
      }, 5000);
    } catch (error) {
      alert("Failed to submit feedback: " + error.message);
    }
  };

  if (!userData || !assignedTeacher || !programInfo) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="dashboard-container">
      {/* Main Content */}
      <main className="main-content">
        <header className="dashboard-header">
          <div className="dashboard-header-content">
            {/* <img src="logo192.png" alt="Yoga Karega India" className="dashboard-logo" /> */}
            <div>
              <h1>Welcome, {userData.email}</h1>
              <p>Track your yoga journey and stay connected with your teacher</p>
            </div>
            {/* <button onClick={handleLogout} className="logout-btn">
              🚪 Logout
            </button> */}
          </div>
        </header>

        {/* User Profile Card */}
        <section className="dashboard-section">
          <h2>My Profile</h2>
          <div className="card-grid">
            <div className="card profile-card">
              <div className="card-header">
                <h3>Personal Information</h3>
                <button onClick={handleProfileUpdate} className="btn btn-sm btn-primary">
                  Edit Profile
                </button>
              </div>
              <div className="card-content">
                <div className="profile-info">
                  <div className="info-item">
                    <label>Email:</label>
                    <span>{userData.email}</span>
                  </div>
                  <div className="info-item">
                    <label>Password:</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span>{showPassword ? (userData.password || '********') : '••••••••••'}</span>
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '14px',
                          color: '#667eea'
                        }}
                      >
                        {showPassword ? '👁‍🗨' : '👁'}
                      </button>
                    </div>
                  </div>
                  <div className="info-item">
                    <label>Member Since:</label>
                    <span>{new Date(userData.loginTime).toLocaleDateString()}</span>
                  </div>
                  <div className="info-item">
                    <label>Status:</label>
                    <span className="badge badge-success">Active</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Assigned Teacher Card */}
        <section className="dashboard-section">
          <h2>My Teacher</h2>
          <div className="card-grid">
            <div className="card teacher-card">
              <div className="card-content">
                <div className="teacher-info">
                  <div className="teacher-avatar">
                    <div className="avatar-placeholder">
                      <span>👩‍🏫</span>
                    </div>
                  </div>
                  <div className="teacher-details">
                    <h3>{assignedTeacher.name}</h3>
                    <p className="teacher-specialization">{assignedTeacher.specialization}</p>
                    <p className="teacher-experience">Experience: {assignedTeacher.experience}</p>
                    <div className="teacher-rating">
                      <span>⭐ {assignedTeacher.rating}</span>
                    </div>
                    <div className="teacher-contact">
                      <button
                        onClick={() => window.open(`tel:${assignedTeacher.phone || '+919876543210'}`, '_self')}
                        className="btn btn-sm btn-secondary"
                      >
                        📞 Contact Teacher
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Teacher Feedback Section */}
        <section className="dashboard-section">
          <h2>Feedback from Your Teacher</h2>
          <div className="table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Teacher Name</th>
                  <th>Rating</th>
                  <th>Feedback</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {teacherFeedback.length > 0 ? (
                  teacherFeedback.map(fb => (
                    <tr key={fb.id}>
                      <td><strong>{fb.teacherName}</strong></td>
                      <td>
                        <span className="stars">
                          {'⭐'.repeat(fb.rating)}
                        </span>
                        <span className="rating-text">({fb.rating}/5)</span>
                      </td>
                      <td>{fb.comment}</td>
                      <td>{new Date(fb.date).toLocaleDateString()}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
                      No feedback from your teacher yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Give Feedback to Teacher Section */}
        <section className="dashboard-section">
          <h2>📝 Give Feedback to Teacher</h2>
          {!feedbackGiven ? (
            <div className="card feedback-form-card">
              <div className="card-content">
                <div className="form-group">
                  <label>Teacher</label>
                  <input
                    type="text"
                    value={assignedTeacher ? assignedTeacher.name : ''}
                    readOnly
                    className="form-control"
                    style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="rating-input">Rating</label>
                  <div className="rating-input">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        className={`star-btn ${star <= feedbackData.rating ? 'active' : ''}`}
                        onClick={() => setFeedbackData({ ...feedbackData, rating: star })}
                        title={`${star} star${star !== 1 ? 's' : ''}`}
                      >
                        ⭐
                      </button>
                    ))}
                    <span className="rating-value">({feedbackData.rating}/5)</span>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="comment-input">Feedback Comment</label>
                  <textarea
                    name="comment"
                    value={feedbackData.comment}
                    onChange={handleFeedbackChange}
                    placeholder="Share your feedback about your teacher..."
                    className="form-control"
                    rows="4"
                  />
                </div>

                <button onClick={handleSubmitFeedback} className="btn btn-primary">
                  Submit Feedback
                </button>

                {feedbackSubmitted && (
                  <div className="feedback-message success">
                    ✅ Feedback submitted successfully!
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="card success-message-card">
              <div className="card-content">
                <div className="success-icon">✅</div>
                <h3>Feedback Submitted Successfully!</h3>
                <p>Thank you for providing feedback to <strong>{assignedTeacher?.name}</strong>.</p>
                <p>Your feedback has been recorded and will help improve the teaching experience.</p>
                <div className="feedback-details">
                  <p><strong>Your Feedback:</strong> {givenFeedback[givenFeedback.length - 1]?.comment}</p>
                  <p><strong>Rating:</strong> {'⭐'.repeat(givenFeedback[givenFeedback.length - 1]?.rating || 5)} ({givenFeedback[givenFeedback.length - 1]?.rating || 5}/5)</p>
                  <p><strong>Date:</strong> {new Date(givenFeedback[givenFeedback.length - 1]?.date || feedbackData.date).toLocaleDateString()}</p>
                </div>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default UserDashboard;
